import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FilterList, TuneRounded, ClearAll } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { projectStatuses } from '../../constants/statusOptions';
import { CollapsiblePanel } from '../../components/layout/CollapsiblePanel';
import type { Client } from '../../types/project';

export interface ProjectFilterValues {
  statuses: string[];
  clientKey: string | null;
  startDate: string;
  endDate: string;
}

interface ProjectFiltersProps {
  clients: Client[];
  filters: ProjectFilterValues;
  onFilterChange: (filters: ProjectFilterValues) => void;
  onClear: () => void;
  statusCounts?: Record<string, number>;
}

const STATUS_DOT_COLORS: Record<string, string> = {
  Active: 'statusActive',
  Planning: 'statusPlanning',
  'On Hold': 'statusOnHold',
  Completed: 'statusCompleted',
};

export const ProjectFilters = ({
  clients,
  filters,
  onFilterChange,
  onClear,
  statusCounts,
}: ProjectFiltersProps) => {
  const theme = useTheme();

  const handleStatusToggle = (status: string) => {
    const next = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];
    onFilterChange({ ...filters, statuses: next });
  };

  const handleClientChange = (_event: unknown, value: Client | null) => {
    onFilterChange({ ...filters, clientKey: value?.ClientKey ?? null });
  };

  const getStatusLabel = (status: string): string => {
    if (statusCounts && statusCounts[status] != null) {
      return `${status} (${statusCounts[status]})`;
    }
    return status;
  };

  const activeFilterCount =
    filters.statuses.length +
    (filters.clientKey ? 1 : 0) +
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0);

  const sectionLabelSx = {
    fontWeight: 600,
    fontSize: '0.6875rem',
    color: alpha(theme.palette.text.primary, 0.5),
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    mb: 0.75,
    display: 'block',
  };

  const inputRootSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: alpha(theme.table.stripeBg, 0.7),
      fontSize: '0.8125rem',
      transition: 'all 0.2s ease',
      '& fieldset': { borderColor: 'transparent' },
      '&:hover': {
        backgroundColor: theme.table.stripeBg,
        '& fieldset': { borderColor: alpha(theme.border.color, 0.6) },
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
        '& fieldset': {
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        },
      },
    },
  };

  const expandedContent = (
    <Box sx={{ px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      {/* Client */}
      <Box>
        <Typography variant="caption" sx={sectionLabelSx}>Client</Typography>
        <Autocomplete
          size="small"
          options={clients}
          getOptionLabel={(opt) => opt.ClientName}
          value={clients.find((c) => c.ClientKey === filters.clientKey) ?? null}
          onChange={handleClientChange}
          renderInput={(params) => (
            <TextField {...params} placeholder="All Clients" sx={inputRootSx} />
          )}
        />
      </Box>

      {/* Status */}
      <Box>
        <Typography variant="caption" sx={sectionLabelSx}>Status</Typography>
        <FormGroup>
          {projectStatuses.map((status) => {
            const dotColor =
              (theme.customColors as Record<string, string>)[STATUS_DOT_COLORS[status]] ?? '#9E9E9E';
            const isChecked = filters.statuses.includes(status);
            return (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    onChange={() => handleStatusToggle(status)}
                    sx={{
                      p: 0.5,
                      transition: 'all 0.15s ease',
                      '&.Mui-checked': { color: theme.palette.primary.main },
                    }}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      transition: 'opacity 0.15s ease',
                      opacity: isChecked ? 1 : 0.7,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: dotColor,
                        flexShrink: 0,
                        boxShadow: isChecked
                          ? `0 0 0 2px ${alpha(dotColor, 0.25)}`
                          : 'none',
                        transition: 'box-shadow 0.2s ease',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: isChecked ? 500 : 400,
                      }}
                    >
                      {getStatusLabel(status)}
                    </Typography>
                  </Box>
                }
                sx={{
                  ml: 0,
                  mr: 0,
                  mb: 0.125,
                  borderRadius: '6px',
                  px: 0.25,
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              />
            );
          })}
        </FormGroup>
      </Box>

      {/* Date Range */}
      <Box>
        <Typography variant="caption" sx={sectionLabelSx}>Date Range</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small"
            type="date"
            label="From"
            value={filters.startDate}
            onChange={(e) => onFilterChange({ ...filters, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={inputRootSx}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={filters.endDate}
            onChange={(e) => onFilterChange({ ...filters, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={inputRootSx}
          />
        </Box>
      </Box>

      {/* Clear */}
      <Box
        component="button"
        onClick={onClear}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: theme.palette.text.secondary,
          fontSize: '0.75rem',
          fontWeight: 500,
          fontFamily: 'inherit',
          p: 0.5,
          borderRadius: '6px',
          alignSelf: 'flex-start',
          transition: 'all 0.15s ease',
          '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
          },
        }}
      >
        <ClearAll sx={{ fontSize: 14 }} />
        Clear Filters
      </Box>
    </Box>
  );

  const collapsedContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5, gap: 0.75 }}>
      <Tooltip title="Filters" placement="right" arrow>
        <IconButton
          size="small"
          sx={{
            width: 34,
            height: 34,
            borderRadius: '8px',
            color: activeFilterCount > 0
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <TuneRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      {activeFilterCount > 0 && (
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            borderRadius: '10px',
            px: 0.625,
            py: 0.125,
            fontSize: '0.6rem',
            fontWeight: 700,
            lineHeight: 1.4,
            minWidth: 16,
            textAlign: 'center',
          }}
        >
          {activeFilterCount}
        </Box>
      )}
    </Box>
  );

  return (
    <CollapsiblePanel
      title="Filters"
      titleIcon={<FilterList sx={{ fontSize: 16, color: alpha(theme.palette.text.primary, 0.45) }} />}
      expandedWidth={224}
      collapsedContent={collapsedContent}
      alignSelf="stretch"
    >
      {expandedContent}
    </CollapsiblePanel>
  );
};
